import React, { useState, useEffect } from 'react';
import { storage } from '../firebase/config';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import './BackgroundSelector.css';

const BackgroundSelector = ({ selectedBackground, onBackgroundSelect, isVisible, onClose }) => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isVisible) {
      loadBackgroundVideos();
    }
  }, [isVisible]);

  const loadBackgroundVideos = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Reference to the background-thumbnails folder in Firebase Storage
      const thumbnailsRef = ref(storage, 'background-thumbnails');
      
      // List all files in the background-thumbnails folder
      const result = await listAll(thumbnailsRef);
      
      // Process each thumbnail file
      const videoPromises = result.items.map(async (itemRef) => {
        try {
          const downloadURL = await getDownloadURL(itemRef);
          const fileName = itemRef.name;
          
          // Extract video name by removing .jpg extension
          const videoName = fileName.replace(/\.jpg$/i, '');
          
          return {
            id: videoName,
            name: videoName,
            thumbnailUrl: downloadURL,
            fileName: fileName
          };
        } catch (error) {
          console.warn(`Failed to get download URL for ${itemRef.name}:`, error);
          return null;
        }
      });

      // Wait for all promises and filter out any null results
      const videoResults = await Promise.all(videoPromises);
      const validVideos = videoResults.filter(video => video !== null);
      
      // Sort videos by name for consistent ordering
      validVideos.sort((a, b) => a.name.localeCompare(b.name));
      
      setVideos(validVideos);
    } catch (error) {
      console.error('Error loading background videos:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackgroundSelect = (selection) => {
    onBackgroundSelect(selection);
    onClose();
  };

  const getDisplayName = (background) => {
    if (!background || background.type === 'black') {
      return 'Black Background';
    }
    const video = videos.find(v => v.id === background.videoId);
    return video ? video.name : 'Unknown Video';
  };

  if (!isVisible) return null;

  return (
    <div className="background-selector-overlay">
      <div className="background-selector-modal">
        <div className="background-selector-header">
          <h3>Select Video Background</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="background-selector-content">
          {isLoading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading background videos...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>Error: {error}</p>
              <button onClick={loadBackgroundVideos} className="retry-button">
                Try Again
              </button>
            </div>
          )}

          {!isLoading && !error && (
            <div className="background-options">
              {/* Black Background Option */}
              <div 
                className={`background-option ${(!selectedBackground || selectedBackground.type === 'black') ? 'selected' : ''}`}
                onClick={() => handleBackgroundSelect({ type: 'black' })}
              >
                <div className="background-thumbnail black-background">
                  <span>No Background</span>
                </div>
                <p>Black Background</p>
              </div>

              {/* Video Background Options */}
              {videos.map((video) => (
                <div 
                  key={video.id}
                  className={`background-option ${selectedBackground?.videoId === video.id ? 'selected' : ''}`}
                  onClick={() => handleBackgroundSelect({ type: 'video', videoId: video.id })}
                >
                  <div className="background-thumbnail">
                    <img 
                      src={video.thumbnailUrl}
                      alt={video.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="thumbnail-fallback" style={{ display: 'none' }}>
                      <span>{video.name}</span>
                    </div>
                  </div>
                  <p>{video.name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="background-selector-footer">
          <div className="current-selection">
            Current: {getDisplayName(selectedBackground)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundSelector; 